/**
 * Tabela de últimos N onboarding jobs.
 *
 * - Status badges color-coded (success/warning/danger).
 * - Inline actions: Retry (FAILED/CANCELLED), Validar (NEEDS_VALIDATION).
 * - "Ver job" → placeholder link (modal será adicionado quando tivermos
 *   detalhe de jobs UI completo no Lote 7/8).
 */
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Skeleton,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconRefresh, IconCheck, IconListDetails } from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import dayjs from 'dayjs'
import { ztnaApi } from '../api'
import type { JobStatus, OnboardingHistoryItem } from '../types'
import { JobDetailDrawer } from './JobDetailDrawer'

interface Props {
  rows?: OnboardingHistoryItem[]
  isLoading?: boolean
}

function statusColor(s: JobStatus): string {
  if (s === 'success') return 'green'
  if (s === 'failed' || s === 'cancelled') return 'red'
  if (s === 'needs_validation') return 'yellow'
  if (s === 'running' || s === 'pending' || s === 'cancelling') return 'blue'
  return 'gray'
}

function statusLabel(s: JobStatus): string {
  if (s === 'needs_validation') return 'NEEDS VALIDATION'
  return s.toUpperCase()
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return '—'
  if (seconds < 60) return `${seconds}s`
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}m ${s.toString().padStart(2, '0')}s`
}

export function OnboardingHistoryTable({ rows, isLoading }: Props) {
  const qc = useQueryClient()
  const [detailJobId, setDetailJobId] = useState<number | null>(null)

  const retryMutation = useMutation({
    mutationFn: (id: number) => ztnaApi.retryJob(id),
    onSuccess: (data) => {
      notifications.show({
        color: 'teal',
        title: 'Retry submetido',
        message: `Novo job #${data.job_id} criado.`,
      })
      qc.invalidateQueries({ queryKey: ['ztna'] })
    },
    onError: (err: any) => {
      const status = err?.response?.status
      notifications.show({
        color: 'red',
        title: 'Erro no retry',
        message: status === 409 ? 'Já existe job ativo equivalente' : 'Falha ao reenviar job',
      })
    },
  })

  const validateMutation = useMutation({
    mutationFn: (id: number) => ztnaApi.validateJob(id),
    onSuccess: () => {
      notifications.show({
        color: 'teal',
        title: 'Job validado',
        message: 'Status atualizado para SUCCESS.',
      })
      qc.invalidateQueries({ queryKey: ['ztna'] })
    },
    onError: (err: any) => {
      const status = err?.response?.status
      notifications.show({
        color: 'red',
        title: 'Erro ao validar',
        message: status === 403 ? 'Apenas admin pode validar' : 'Falha ao validar job',
      })
    },
  })

  return (
    <Card>
      <Group justify="space-between" mb="sm">
        <Text fw={700}>Onboarding history</Text>
        <Text size="xs" c="dimmed">
          últimos {rows?.length ?? 0} jobs
        </Text>
      </Group>
      {isLoading ? (
        <Stack gap="xs">
          <Skeleton height={28} />
          <Skeleton height={28} />
          <Skeleton height={28} />
        </Stack>
      ) : !rows || rows.length === 0 ? (
        <Alert color="gray" variant="light">
          Nenhum job de onboarding ainda.
        </Alert>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Data</Table.Th>
              <Table.Th>App</Table.Th>
              <Table.Th>Tenant</Table.Th>
              <Table.Th>User</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Duração</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => (
              <Table.Tr key={row.job_id}>
                <Table.Td ff="monospace" fz="xs">
                  {dayjs(row.created_at).format('YYYY-MM-DD HH:mm')}
                </Table.Td>
                <Table.Td>
                  <Text fw={600} size="sm">{row.slug || `job-${row.job_id}`}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    #{row.tenant_id} {row.environment ? `(${row.environment})` : ''}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{row.user_email ?? `user#${row.user_id}`}</Text>
                </Table.Td>
                <Table.Td>
                  <Badge
                    color={statusColor(row.status)}
                    variant="light"
                    aria-label={`status: ${row.status}`}
                  >
                    {statusLabel(row.status)}
                  </Badge>
                </Table.Td>
                <Table.Td ff="monospace" fz="xs">
                  {formatDuration(row.duration_seconds)}
                </Table.Td>
                <Table.Td>
                  <RowActions
                    row={row}
                    onRetry={() => retryMutation.mutate(row.job_id)}
                    onValidate={() => validateMutation.mutate(row.job_id)}
                    onDetails={() => setDetailJobId(row.job_id)}
                    retrying={retryMutation.isPending && retryMutation.variables === row.job_id}
                    validating={
                      validateMutation.isPending &&
                      validateMutation.variables === row.job_id
                    }
                  />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      <JobDetailDrawer
        jobId={detailJobId}
        opened={detailJobId != null}
        onClose={() => setDetailJobId(null)}
      />
    </Card>
  )
}

interface RowActionsProps {
  row: OnboardingHistoryItem
  onRetry: () => void
  onValidate: () => void
  onDetails: () => void
  retrying: boolean
  validating: boolean
}

function RowActions({
  row,
  onRetry,
  onValidate,
  onDetails,
  retrying,
  validating,
}: RowActionsProps) {
  return (
    <Group gap="xs" wrap="nowrap" justify="flex-end">
      {(row.status === 'failed' || row.status === 'cancelled') && (
        <Button
          size="xs"
          variant="default"
          leftSection={<IconRefresh size={14} />}
          loading={retrying}
          onClick={onRetry}
        >
          Retry
        </Button>
      )}
      {row.status === 'needs_validation' && (
        <Button
          size="xs"
          variant="default"
          color="yellow"
          leftSection={<IconCheck size={14} />}
          loading={validating}
          onClick={onValidate}
        >
          Validar
        </Button>
      )}
      <Tooltip label="Ver detalhes / logs">
        <ActionIcon
          variant="subtle"
          color="gray"
          aria-label="Ver detalhes do job"
          onClick={onDetails}
        >
          <IconListDetails size={16} />
        </ActionIcon>
      </Tooltip>
    </Group>
  )
}

export default OnboardingHistoryTable
